import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { transactionService } from '@/services/api';
import { Activity, DollarSign, TrendingUp, CreditCard, Wallet, FileText } from 'lucide-react';
import axios from 'axios';
import MerchantAnalytics from '@/components/MerchantAnalytics';
import DomiciliacionPendingPanel from '@/components/DomiciliacionPendingPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [projectInfo, setProjectInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const projectId = localStorage.getItem('activeProjectId');

      if (projectId && projectId !== 'all') {
        // Cargar stats del proyecto específico
        const [projectRes, statsRes, transactionsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/projects/${projectId}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${BACKEND_URL}/api/projects/${projectId}/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          transactionService.getMerchantTransactions(10, 0)
        ]);

        setProjectInfo(projectRes.data);
        setStats(statsRes.data);
        setTransactions(transactionsRes);
      } else {
        // Vista general de todos los proyectos
        const dashboardData = await transactionService.getDashboard();
        setStats(dashboardData.stats);
        setTransactions(await transactionService.getMerchantTransactions(10, 0));
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-xl text-gray-600">Cargando...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Project Info Banner */}
        {projectInfo && (
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 mb-6 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-bold text-lg" style={{ color: '#003594' }}>{projectInfo.project_name}</h2>
                <p className="text-sm text-gray-600">{projectInfo.project_description || 'Sin descripción'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-600">Comisión</p>
                <p className="text-2xl font-bold" style={{ color: '#003594' }}>{projectInfo.fee_percentage}%</p>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Avanzados */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#051C2C' }}>
            Resumen de Rendimiento
          </h2>
          <MerchantAnalytics />
        </div>

        {/* Stats Grid */}
        <h2 className="text-xl font-bold mb-4" style={{ color: '#051C2C' }}>
          Estadísticas Generales
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="stats-grid">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#E8F3FA' }}>
                <DollarSign className="w-6 h-6" style={{ color: '#003594' }} />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Volumen Total</h3>
            <p className="text-2xl font-bold mt-1" style={{ color: '#051C2C' }}>
              ${stats?.total_volume?.toFixed(2) || '0.00'}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#E8F3FA' }}>
                <Activity className="w-6 h-6" style={{ color: '#008AD8' }} />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Transacciones</h3>
            <p className="text-2xl font-bold mt-1" style={{ color: '#051C2C' }}>
              {stats?.total_transactions || 0}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#D5F5E3' }}>
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Completadas</h3>
            <p className="text-2xl font-bold mt-1 text-green-600">
              {stats?.completed_transactions || 0}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#D1FAE5' }}>
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Balance en Wallets</h3>
            <p className="text-2xl font-bold mt-1 text-green-600">
              ${stats?.total_wallet_balance?.toFixed(2) || '0.00'}
            </p>
          </div>
        </div>

        {/* Quick Access Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <button
            onClick={() => navigate('/wallets')}
            className="p-6 bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all text-left"
          >
            <Wallet className="w-8 h-8 mb-3" style={{ color: '#008AD8' }} />
            <h3 className="font-semibold mb-1" style={{ color: '#051C2C' }}>Wallets</h3>
            <p className="text-sm text-gray-600">Gestiona tus billeteras digitales</p>
          </button>

          <button
            onClick={() => navigate('/orders')}
            className="p-6 bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all text-left"
          >
            <FileText className="w-8 h-8 mb-3" style={{ color: '#003594' }} />
            <h3 className="font-semibold mb-1" style={{ color: '#051C2C' }}>Órdenes de Pago</h3>
            <p className="text-sm text-gray-600">Crea y gestiona órdenes con dispersión</p>
          </button>

          <button
            onClick={() => navigate('/documentation')}
            className="p-6 bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all text-left"
          >
            <CreditCard className="w-8 h-8 mb-3" style={{ color: '#008AD8' }} />
            <h3 className="font-semibold mb-1" style={{ color: '#051C2C' }}>Métodos de Pago</h3>
            <p className="text-sm text-gray-600">Configura tus métodos de pago</p>
          </button>
        </div>

        {/* Domiciliación en liquidación (PE) */}
        <DomiciliacionPendingPanel />

        {/* Recent Transactions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-testid="transactions-table">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold" style={{ color: '#051C2C' }}>
              Transacciones Recientes
            </h2>
          </div>

          {transactions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No hay transacciones todavía</p>
              <p className="text-sm mt-1">Las transacciones aparecerán aquí cuando uses tu API key</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Método
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((tx) => (
                    <tr key={tx.transaction_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
                        {tx.transaction_id.slice(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                        {tx.currency} {tx.amount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {tx.payment_method.replace('_', ' ').toUpperCase()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          tx.status === 'completed' ? 'bg-green-100 text-green-800' :
                          tx.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          tx.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {tx.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {new Date(tx.created_at).toLocaleDateString('es-VE')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
